import React, { useEffect, useState } from "react";
import styles from "./FeedItemOptionsDropdown.module.css";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import classNames from "classnames";

import TextInput from "../TextInput";
import PinLevelPermissionCheckboxList from "../PinLevelPermissionCheckboxList";
import CheckboxListDropdown from "../CheckboxListDropdown";
import PlatformCheckboxList from "../PlatformCheckboxList";
import StatusRadioGroup from "../StatusRadioGroup";
import SpanInput from "../SpanInput";
import TestingIBOPreview from "../TestingIBOPreview";
import ChevronButton from "../ChevronButton";
import FeedConfigBar from "../FeedConfigBar";
import {
  useFeedItemOptionsDropdown,
  useFeedItemOptionsDropdownGroup,
} from "./useFeedItemOptionsDropdown";
import { mapApiResponseToFeedItem } from "../../utils/mapFeedItems";

import MTLockIcon from "../../assests/Management_Team_Lock.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckSquare,
  faGem,
  faMinusCircle,
  faPenSquare,
} from "@fortawesome/free-solid-svg-icons";

// Add mapping constants
const STATUS_MAP = {
  0: "Unpublished",
  1: "Published",
  2: "Archived",
  3: "Storaged",
};

const PINLEVEL_MAP = {
  Guest: 1, //Not used
  AssociateIbo: "IBO",
  Platinum: "PLA",
  Emerald: "EM",
  Diamond: "DIA",
};

const PINLEVELREVERSE_MAP = {
  1: "Guest", //Not used
  IBO: "AssociateIbo",
  PLA: "Platinum",
  EM: "Emerald",
  DIA: "Diamond",
};

// Map pin levels to their corresponding group names (exact names from API)
const PINLEVEL_TO_GROUP_NAME_MAP = {
  AssociateIbo: "Active Associate IBOs",
  Platinum: "Active Platinums",
  Emerald: "Active Emeralds",
  Diamond: "Active Diamonds",
};

// Reverse map: group names to pin levels
const GROUP_NAME_TO_PINLEVEL_MAP = {
  "Active Associate IBOs": "AssociateIbo",
  "Active Platinums": "Platinum",
  "Active Emeralds": "Emerald",
  "Active Diamonds": "Diamond",
};

const PLATFORM_MAP = {
  Web: "PC",
  PC: "PC", // optional, just for idempotency
  iOS: "Apple",
  Apple: "Apple",
  Android: "Android",
};

const FEEDCONFIGBAR_PLATFORM_MAP = {
  Web: "PC",
  PC: "PC", // optional, just for idempotency
  Apple: "iOS",
  Android: "Android",
};

const dropdownReunionOptions = [
  {
    name: "Puryear Family Reunion",
    id: 8192,
  },
  {
    name: "Duncan Family Reunion",
    id: 15,
  },
  {
    name: "Rice Family Reunion",
    id: 1048576,
  },
  {
    name: "Baker Family Reunion",
    id: 16,
  },
  {
    name: "Whalen Family Reunion",
    id: 17,
  },
];

/**
 * Interactive component.
 * Dropdown that allows an item's feed configurations to be modified or created.
 */
export function FeedItemOptionsDropdown({
  isOpen,
  onClick,
  optionsConfig,
  setOptionsConfig,
  isEdit,
}) {
  const {
    data: diamondData,
    isLoading,
    refetch,
  } = useFeedItemOptionsDropdown();

  const { data: groupData } = useFeedItemOptionsDropdownGroup();

  const [diamonds, setDiamonds] = useState([]);

  const [groups, setGroups] = useState([]);

  // console.log("FINAL optionsConfig received:", optionsConfig);

  const normalizedPlatforms = (optionsConfig?.platforms || []).map(
    (platform) => PLATFORM_MAP[platform] || platform
  );

  useEffect(() => {
    if (diamondData) {
      setDiamonds(diamondData);
    }
  }, [diamondData]);

  useEffect(() => {
    if (groupData) {
      setGroups(groupData);
    }
  }, [groupData]);

  // Get the names from diamond options
  const diamondNames = diamonds.map((diamond) => {
    return diamond.name;
  });

  //Get the names from the group options
  const groupNames = groups.map((group) => {
    return group.name;
  });

  const reunions = dropdownReunionOptions.map((reunion) => {
    return reunion.name;
  });

  // Get the current date
  const current = new Date();
  const date = `${
    current.getMonth() + 1
  }/${current.getDate()}/${current.getFullYear()}`;

  const [selectedReunionOptions, setSelectedReunionOptions] = useState(
    optionsConfig.familyReunions || []
  );
  const [selectedDiamondOptions, setSelectedDiamondOptions] = useState(
    optionsConfig.diamonds || []
  );
  const [selectedGroupOptions, setSelectedGroupOptions] = useState(
    optionsConfig.groups || []
  );

  const [sendUpdate, setSendUpdate] = useState(
    optionsConfig.shouldSendUpdateIcon === true
  );

  const [publishStatus, setPublishStatus] = useState(
    optionsConfig.status == 0
      ? "Unpublished"
      : optionsConfig.status == 1
      ? "Published"
      : optionsConfig.status == 2
      ? "Archived"
      : "Storaged"
  );

  const [approveStatus, setApproveStatus] = useState(
    optionsConfig.managementApproved == false
      ? "Let Diamonds Approve"
      : optionsConfig.status == true
      ? "Management Approved"
      : "Management Approved" || ""
  );

  const [publishDate, setPublishDate] = useState(
    dayjs(optionsConfig.availableOn).format("YYYY-MM-DD") || date
  );
  const [publishTime, setPublishTime] = useState(
    dayjs(optionsConfig.availableOn).format("HH:mm") || ""
  );
  const [priority, setPriority] = useState(optionsConfig.priority || 0);

  const [unPublishDate, setUnPublishDate] = useState(
    dayjs(optionsConfig.unpublishOn).format("YYYY-MM-DD") || date
  );
  const [unPublishTime, setUnPublishTime] = useState(
    dayjs(optionsConfig.unpublishOn).format("HH:mm") || ""
  );
  //Not sure what this is yet
  const [removalAction, setRemovalAction] = useState(
    optionsConfig.unpublishType === 0
      ? "Unpublished"
      : optionsConfig.unpublishType === 1
      ? "Archived"
      : "Unknown"
  );
  const [iboNumber, setIboNumber] = useState(
    optionsConfig.previewIBO ?? optionsConfig.previewIbo ?? 0
  );

  const [titleValue, setTitleValue] = useState(optionsConfig.title);

  const [checkedPermissions, setCheckedPermissions] = useState(
    optionsConfig.pinLevelPermissions
  );

  const [checkedPlatforms, setCheckedPlatforms] = useState(
    optionsConfig.platforms || []
  ); // Add safeguard for 'undefined'

  // Sync pin level permissions with their corresponding groups
  useEffect(() => {
    const currentPermissions = optionsConfig.pinLevelPermissions || [];
    const groupsToAdd = currentPermissions
      .map((perm) => PINLEVEL_TO_GROUP_NAME_MAP[perm])
      .filter(Boolean);

    // Only update if there's a change
    const currentGroups = optionsConfig.groups || [];
    const groupsHaveChanged =
      groupsToAdd.length !== currentGroups.length ||
      !groupsToAdd.every((g) => currentGroups.includes(g));

    if (groupsHaveChanged) {
      const updatedOptions = {
        ...optionsConfig,
        groups: groupsToAdd,
      };
      setSelectedGroupOptions(groupsToAdd);
      setOptionsConfig(updatedOptions);
    }
  }, [optionsConfig.pinLevelPermissions]);

  const handleReunionChecked = (e) => {
    const value = e.target.name;
    let newInfo = { ...optionsConfig };

    let currentFamilyReunions = newInfo.familyReunions || [];
    if (typeof currentFamilyReunions === "string") {
      currentFamilyReunions = currentFamilyReunions.split(",").filter(Boolean);
    }

    if (currentFamilyReunions.includes(value)) {
      newInfo.familyReunions = currentFamilyReunions.filter((d) => d !== value);
    } else {
      newInfo.familyReunions = [...currentFamilyReunions, value];
    }

    setSelectedReunionOptions(newInfo.familyReunions);
    setOptionsConfig(newInfo);
  };

  const handleDiamondChecked = (e) => {
    const value = e.target.name;
    let newInfo = { ...optionsConfig };

    let currentDiamonds = newInfo.diamonds || [];
    if (typeof currentDiamonds === "string") {
      currentDiamonds = currentDiamonds.split(",").filter(Boolean);
    }

    if (currentDiamonds.includes(value)) {
      newInfo.diamonds = currentDiamonds.filter((d) => d !== value);
    } else {
      newInfo.diamonds = [...currentDiamonds, value];
    }

    setSelectedDiamondOptions(newInfo.diamonds);
    setOptionsConfig(newInfo);
  };

  const handleGroupChecked = (e) => {
    const value = e.target.name;
    let newInfo = { ...optionsConfig };

    let currentGroups = newInfo.groups || [];
    if (typeof currentGroups === "string") {
      currentGroups = currentGroups.split(",").filter(Boolean);
    }

    // Get the corresponding pin level for this group
    const pinLevelToToggle = GROUP_NAME_TO_PINLEVEL_MAP[value];

    if (currentGroups.includes(value)) {
      // Unchecking group - also uncheck the corresponding pin level
      newInfo.groups = currentGroups.filter((d) => d !== value);

      if (pinLevelToToggle) {
        let currentPermissions = newInfo.pinLevelPermissions || [];
        newInfo.pinLevelPermissions = currentPermissions.filter(
          (perm) => perm !== pinLevelToToggle
        );
        setCheckedPermissions(newInfo.pinLevelPermissions);
      }
    } else {
      // Checking group - also check the corresponding pin level
      newInfo.groups = [...currentGroups, value];

      if (pinLevelToToggle) {
        let currentPermissions = newInfo.pinLevelPermissions || [];
        if (!currentPermissions.includes(pinLevelToToggle)) {
          newInfo.pinLevelPermissions = [
            ...currentPermissions,
            pinLevelToToggle,
          ];
          setCheckedPermissions(newInfo.pinLevelPermissions);
        }
      }
    }

    setSelectedGroupOptions(newInfo.groups);
    setOptionsConfig(newInfo);
  };

  const handleDropdown = () => {
    // setCurrentDropdown(!isOpen);
    setOptionsConfig(
      {
        title: titleValue,
        permissions: checkedPermissions,
        familyReunions: selectedReunionOptions,
        diamonds: selectedDiamondOptions,
        groups: selectedGroupOptions,
        incompatiblePlatforms: checkedPlatforms,
        publishStatus: publishStatus,
        publishDate: {
          date: publishDate,
          time: publishTime,
          priority: priority,
        },
        removalDate: {
          date: unPublishDate,
          time: unPublishTime,
        },
        removalDateAction: removalAction,
        approvalStatus: approveStatus,
      },
      "optionsConfig"
    );
  };

  const handleInputChange = (name, value) => {
    let newInfo = { ...optionsConfig };

    newInfo[name] = value;

    setOptionsConfig(newInfo, "optionsConfig");

    setTitleValue(value);
  };

  const handlePermissionChange = (value) => {
    const mappedValue = PINLEVELREVERSE_MAP[value] || value;
    let newInfo = { ...optionsConfig };
    const currentPermissions = newInfo.pinLevelPermissions || [];

    // Get the corresponding group name for this pin level
    const groupNameToToggle = PINLEVEL_TO_GROUP_NAME_MAP[mappedValue];

    if (currentPermissions.includes(mappedValue)) {
      // Removing permission - also remove the corresponding group
      newInfo.pinLevelPermissions = currentPermissions.filter(
        (perm) => perm !== mappedValue
      );

      // Remove the corresponding group if it exists
      if (groupNameToToggle) {
        let currentGroups = newInfo.groups || [];
        newInfo.groups = currentGroups.filter((g) => g !== groupNameToToggle);
        setSelectedGroupOptions(newInfo.groups);
      }
    } else {
      // Adding permission - also add the corresponding group
      newInfo.pinLevelPermissions = [...currentPermissions, mappedValue];

      // Add the corresponding group if it exists
      if (groupNameToToggle) {
        let currentGroups = newInfo.groups || [];
        if (!currentGroups.includes(groupNameToToggle)) {
          newInfo.groups = [...currentGroups, groupNameToToggle];
          setSelectedGroupOptions(newInfo.groups);
        }
      }
    }

    setCheckedPermissions(newInfo.pinLevelPermissions);
    setOptionsConfig(newInfo);
  };

  const handlePlatformChange = (value) => {
    let newInfo = { ...optionsConfig };
    const currentPermissions = newInfo.platforms || [];

    if (currentPermissions.includes(value)) {
      newInfo.platforms = currentPermissions.filter((perm) => perm !== value);
    } else {
      newInfo.platforms = [...currentPermissions, value];
    }

    setCheckedPlatforms(newInfo.platforms);
    setOptionsConfig(newInfo);
  };

  const handleStatusChange = (value) => {
    let newInfo = { ...optionsConfig };

    const status =
      value == "Unpublished"
        ? 0
        : value == "Published"
        ? 1
        : value == "Archived"
        ? 2
        : 3;

    newInfo.status = status;

    setOptionsConfig(newInfo, "optionsConfig");

    setPublishStatus(value ? value : "Unpublished");
  };

  const handleRemovalChange = (value) => {
    let newInfo = { ...optionsConfig };

    const status = value == "Unpublished" ? 0 : value == "Archived" ? 2 : 0;

    newInfo.unpublishType = status;

    setOptionsConfig(newInfo, "optionsConfig");

    setRemovalAction(value ? value : "Unpublished");
  };

  const handleApprovalChange = (value) => {
    let newInfo = { ...optionsConfig };

    const status =
      value == "Let Diamonds Approve"
        ? false
        : value == "Management Approved"
        ? true
        : false;

    newInfo.managementApproved = status;

    setOptionsConfig(newInfo, "optionsConfig");

    setApproveStatus(value);
  };

  const getIsoDateTime = (date, time) => {
    if (!date) return null;

    const validTime = time || "00:00";

    const dateTimeString = `${date}T${validTime}`;

    const newDate = new Date(dateTimeString);

    if (isNaN(newDate.getTime())) {
      return null;
    }
    return newDate.toISOString();
  };

  const handleSendUpdate = (e) => {
    const isChecked = e.target.checked;
    setSendUpdate(isChecked);

    setOptionsConfig({ ...optionsConfig, shouldSendUpdateIcon: isChecked });
  };

  const handlePublishDateChange = (e) => {
    const newDate = e.target.value;
    setPublishDate(newDate);
    const isoString = getIsoDateTime(newDate, publishTime);
    setOptionsConfig({ ...optionsConfig, availableOn: isoString });
  };

  const handlePublishTimeChange = (e) => {
    const newTime = e.target.value;
    setPublishTime(newTime);
    const isoString = getIsoDateTime(publishDate, newTime);
    setOptionsConfig({ ...optionsConfig, availableOn: isoString });
  };

  const handleUnpublishDateChange = (e) => {
    const newDate = e.target.value;
    setUnPublishDate(newDate);

    const newTime = "00:00";
    setUnPublishTime(newTime);

    const isoString = getIsoDateTime(newDate, newTime);
    setOptionsConfig({ ...optionsConfig, unpublishOn: isoString });
  };

  const handleUnpublishTimeChange = (e) => {
    const newTime = e.target.value;
    setUnPublishTime(newTime);
    const isoString = getIsoDateTime(unPublishDate, newTime);
    setOptionsConfig({ ...optionsConfig, unpublishOn: isoString });
  };

  return (
    <section className={styles.container}>
      {!isOpen ? (
        <div className={styles.headerContainer}>
          <div
            className={classNames(styles.content, {
              [styles["dropdown-container"]]: isOpen,
            })}
            onClick={() => {
              if (!isOpen) {
                onClick(isOpen ? "close" : "options");
              }
            }}
          >
            <div
              className={classNames({
                [styles.hidden]: isOpen,
              })}
            >
              <h3 className={styles.headerTitle}>{titleValue}</h3>

              {/* Updated with mapping constants (just below imports) */}

              <FeedConfigBar
                publishStatus={
                  STATUS_MAP[optionsConfig.status] || "Unpublished"
                }
                date={publishDate} // Update to actual parsed and formatted date from state
                unpublishDate={unPublishDate}
                signal={optionsConfig.priority}
                pinLevelPermissions={optionsConfig.pinLevelPermissions}
                platforms={optionsConfig.platforms}
              />
            </div>
          </div>
          <div className={styles.chevron}>
            <ChevronButton
              onClick={() => onClick(isOpen ? "close" : "options")}
              orientation={isOpen ? "down" : "right"}
            />
          </div>
        </div>
      ) : (
        <div>
          <div className={styles.dropdownContainer}>
            <div className={styles.inputContainer}>
              <div className={styles.titleContainer}>
                <TextInput
                  label={"Title"}
                  placeholder={" * Standard Content Title * "}
                  value={titleValue}
                  setValue={(e) => handleInputChange("title", e.target.value)}
                  className={styles.titleInput}
                />
              </div>
              <div className={styles.chevron}>
                <ChevronButton
                  onClick={() => onClick(isOpen ? "close" : "options")}
                  orientation={isOpen ? "down" : "right"}
                />
              </div>
            </div>

            <div className={styles.marginTopPin}>
              <PinLevelPermissionCheckboxList
                checkedPermissions={checkedPermissions}
                setCheckedPermissions={handlePermissionChange}
              />
            </div>

            <div className={styles.marginTop}>
              <CheckboxListDropdown
                dropdownText={
                  "Family Reunion Diamond Groups (Will only show to these Diamonds and their LOS)"
                }
                dropdownOptions={reunions}
                checkedOptions={selectedReunionOptions}
                setCheckedOptions={handleReunionChecked}
              />

              <div className={styles.checkboxContainer}>
                <CheckboxListDropdown
                  dropdownText={
                    "Diamonds (Will only show to these diamonds and their LOS)"
                  }
                  dropdownOptions={diamondNames}
                  checkedOptions={selectedDiamondOptions}
                  setCheckedOptions={handleDiamondChecked}
                />
              </div>

              <div className={styles.checkboxContainer}>
                <CheckboxListDropdown
                  dropdownText={"Groups (Will only show to these groups)"}
                  dropdownOptions={groupNames}
                  checkedOptions={selectedGroupOptions}
                  setCheckedOptions={handleGroupChecked}
                />
              </div>
            </div>

            <div className={styles.marginTop}>
              <PlatformCheckboxList
                checkedPlatforms={normalizedPlatforms} // Pass normalized platforms
                setCheckedPlatforms={handlePlatformChange}
              />
            </div>

            <div className={styles.publishContainer}>
              <StatusRadioGroup
                orientation={"vertical"}
                label={"Publish Status"}
                options={[
                  {
                    optionTitle: "Unpublished",
                    optionColor: "blue",
                    optionIcon: (
                      <FontAwesomeIcon
                        style={{ marginRight: "5px" }}
                        icon={faPenSquare}
                      />
                    ),
                  },
                  {
                    optionTitle: "Published",
                    optionColor: "green",
                    optionIcon: (
                      <FontAwesomeIcon
                        style={{ marginRight: "5px" }}
                        icon={faCheckSquare}
                      />
                    ),
                  },
                  {
                    optionTitle: "Archived",
                    optionColor: "red",
                    optionIcon: (
                      <FontAwesomeIcon
                        style={{ marginRight: "5px" }}
                        icon={faMinusCircle}
                      />
                    ),
                  },
                  { optionTitle: "Storaged", optionColor: "red" },
                ]}
                currentStatus={publishStatus}
                setCurrentStatus={(optionTitle) =>
                  handleStatusChange(optionTitle)
                }
              />

              {isEdit === false && (
                <div className={styles.sendUpdate} onClick={() => {}}>
                  <label htmlFor={"Send Update"} className={styles.updatelabel}>
                    <input
                      type="checkbox"
                      name={"Send Update"}
                      checked={sendUpdate}
                      onChange={handleSendUpdate}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkmark}></span>
                    <span className={styles.label}>{"Send Update"}</span>
                  </label>
                </div>
              )}
            </div>

            <div className={styles.marginTop}>
              <h5 className={styles.title}>Publish On</h5>
              <div className={styles.dateContainer}>
                <div className={styles.dateItem}>
                  <SpanInput
                    inputType={"date"}
                    inputLabel={"Date:"}
                    currentValue={publishDate}
                    setCurrentValue={handlePublishDateChange}
                  />
                </div>
                <div className={styles.timeItem}>
                  <SpanInput
                    inputType={"time"}
                    inputLabel={"Time:"}
                    currentValue={publishTime}
                    setCurrentValue={handlePublishTimeChange}
                  />
                </div>

                {publishStatus === null ||
                  (publishStatus !== "Unpublished" && (
                    <div className={styles.priorityItem}>
                      <SpanInput
                        inputType={"number"}
                        inputLabel={"Priority:"}
                        currentValue={String(priority)}
                        setCurrentValue={(e) => {
                          const value = e.target.value;
                          setPriority(value);

                          let newInfo = { ...optionsConfig };
                          newInfo.priority = value;
                          setOptionsConfig(newInfo);
                        }}
                      />
                    </div>
                  ))}
              </div>
            </div>

            <div className={styles.marginTop}>
              <h5 className={styles.title}>Unpublish or Archive On</h5>
              <div className={styles.dateContainer}>
                <SpanInput
                  inputType={"date"}
                  inputLabel={"Date:"}
                  currentValue={unPublishDate}
                  setCurrentValue={handleUnpublishDateChange}
                  className={styles.span}
                />
                <SpanInput
                  inputType={"time"}
                  inputLabel={"Time:"}
                  currentValue={unPublishTime}
                  setCurrentValue={handleUnpublishTimeChange}
                />
              </div>
            </div>

            <div className={styles.marginTop}>
              <StatusRadioGroup
                orientation={"horizontal"}
                label={"On the unpublished date:"}
                options={[
                  {
                    optionTitle: "Unpublished",
                    optionColor: "blue",
                    optionIcon: (
                      <FontAwesomeIcon
                        style={{ marginRight: "5px" }}
                        icon={faPenSquare}
                      />
                    ),
                  },

                  {
                    optionTitle: "Archived",
                    optionColor: "red",
                    optionIcon: (
                      <FontAwesomeIcon
                        style={{ marginRight: "5px" }}
                        icon={faMinusCircle}
                      />
                    ),
                  },
                ]}
                //(Unpublish = 0, Archive = 2)
                currentStatus={removalAction}
                setCurrentStatus={(optionTitle) =>
                  handleRemovalChange(optionTitle)
                }
              />
            </div>

            <div className={styles.approvalContainer}>
              <StatusRadioGroup
                orientation={"vertical"}
                label={"Approval Status"}
                options={[
                  {
                    optionTitle: "Management Approved",
                    optionColor: "blue",
                    optionIcon: (
                      <img
                        src={MTLockIcon}
                        alt="lock icon"
                        style={{
                          marginRight: "5px",
                          width: "11px",
                          height: "15px",
                        }}
                      />
                    ),
                  },

                  {
                    optionTitle: "Let Diamonds Approve",
                    optionColor: "green",
                    optionIcon: (
                      <FontAwesomeIcon
                        style={{
                          marginRight: "5px",
                          width: "11px",
                          height: "15px",
                        }}
                        icon={faGem}
                      />
                    ),
                  },
                ]}
                currentStatus={approveStatus}
                setCurrentStatus={(optionTitle) =>
                  handleApprovalChange(optionTitle)
                }
              />
            </div>

            {optionsConfig.status === 0 && (
              <div className={styles.testingContainer}>
                <TestingIBOPreview
                  IBONumber={iboNumber}
                  setIBONumber={(e) => {
                    const value = e.target.value;
                    setIboNumber(value);

                    let newInfo = { ...optionsConfig };
                    newInfo.previewIBO = Number(value || 0);
                    newInfo.previewIbo = Number(value || 0);
                    setOptionsConfig(newInfo);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {!isOpen && <hr className={styles.hr} />}
    </section>
  );
}

FeedItemOptionsDropdown.propTypes = {
  /**
   * Boolean that determines if the dropdown is expanded.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the dropdown is clicked.
   */
  onClick: PropTypes.func,
  /**
   * Object that holds the item's feed configurations.
   */
  optionsConfig: PropTypes.shape({
    title: PropTypes.string,
    permissions: PropTypes.arrayOf(
      PropTypes.oneOf(["IBO", "PLA", "EM", "DIA"])
    ),
    familyReunions: PropTypes.arrayOf(PropTypes.string),
    diamonds: PropTypes.arrayOf(PropTypes.string),
    groups: PropTypes.arrayOf(PropTypes.string),
    incompatiblePlatforms: PropTypes.arrayOf(
      PropTypes.oneOf(["PC", "Apple", "Android"])
    ),
    publishStatus: PropTypes.oneOf([
      "Unpublished",
      "Published",
      "Archived",
      "Storaged",
    ]),
    publishDate: PropTypes.shape({
      date: PropTypes.string,
      time: PropTypes.string,
      priority: PropTypes.number,
    }),
    removalDate: PropTypes.shape({
      date: PropTypes.string,
      time: PropTypes.string,
    }),
    removalDateAction: PropTypes.oneOf(["Unpublished", "Archived"]),
    approvalStatus: PropTypes.oneOf([
      "Management Approved",
      "Let Diamonds Approve",
    ]),
  }),
  /**
   * Sets the values of optionsConfig on change.
   */
  setOptionsConfig: PropTypes.func,
  /**
   * Determines whether the feed is being edited or created.
   */
  isEdit: PropTypes.bool,
  /**
   * Optional boolean that determines if there will be update sent on edit completion.
   */
  sendUpdate: PropTypes.bool,
  /**
   * Sets the value for “sendUpdate.”
   */
  setSendUpdate: PropTypes.func,
};

FeedItemOptionsDropdown.defaultProps = {
  optionsConfig: {
    title: "Title",
    permissions: ["IBO", "PLA", "EM", "DIA"],
    familyReunions: [],
    diamonds: "",
    groups: "",
    incompatiblePlatforms: [],
    publishStatus: "Unpublished",
    publishDate: {
      date: dayjs().subtract(1, "day").format("YYYY-MM-DD"),
      time: dayjs().add(30, "minute").format("H:00"),
      priority: 0,
    },
    removalDate: {
      date: "",
      time: "",
    },
    removalDateAction: "Unpublished",
    approvalStatus: "Let Diamonds Approve",
    pinLevelPermissions: [], // Add pinLevelPermissions to defaultProps to prevent crashing
    platforms: [], // Add platforms to defaultProps to prevent crashing
  },
};
